import { useContext, useEffect, useState } from "react";
import { AuthenticationContext } from "../../../contexts/authentication-context";
import { CompanyContext } from "../../../contexts/company-context";
import { Alert, Form, Modal, Select } from "antd";
import {
  EmployeeDetails,
  ResponseEmployeeBranchAccess,
} from "../../../serviceoperation/entity/EmployeeOperationEntity";
import roleOptions from "../../../Util/constants";
import { EmployeeOperation } from "../../../serviceoperation/EmployeeOperation";
import { LocationType } from "./types";
import { BranchOperation } from "../../../serviceoperation/BranchOperation";

interface Props {
  employee: EmployeeDetails | null;
  open: boolean;
  onCreate: (values: any) => void;
  onCancel: () => void;
}

interface BranchAccessFormValues {
  branchId: number;
  roleId: number;
}

const AddAccess = ({ employee, open, onCreate, onCancel }: Props) => {
  const [form] = Form.useForm();
  const { selectedCompany } = useContext(CompanyContext);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [location, setLocation] = useState<LocationType[]>([]);

  const userInfo = useContext(AuthenticationContext)?.userInfo;

  const addEmployeeAccess = async (
    values: BranchAccessFormValues
  ): Promise<ResponseEmployeeBranchAccess | null> => {
    setConfirmLoading(true);
    let success = false;

    const employeeOps = new EmployeeOperation();
    let result: ResponseEmployeeBranchAccess | null = null;

    await employeeOps
      .PostEmployeeAcessAsync(
        selectedCompany?.corporationId as number,
        employee?.employeeId as string,
        {
          branchAccessRules: [
            {
              branchId: values.branchId,
              roleId: values.roleId,
            },
          ],
        }
      )
      .then((x) => {
        success =
          x.statusCode != undefined && x.statusCode.toString() === "201";

        // Set Error Message for Conflict.
        if (x.statusCode != undefined && x.statusCode.toString() === "409") {
          setShowError(true);
          setErrorMessage("TimeOff reason already exist");
        }
        result = success
          ? ({
              accessId: x.data?.accessId,
              branchId: values.branchId,
              roleId: values.roleId,
            } as ResponseEmployeeBranchAccess)
          : null;
      });
    setConfirmLoading(false);
    return success ? result : null;
  };

  useEffect(() => {
    const branchOps = new BranchOperation();
    branchOps
      .getBranchAsync(selectedCompany?.corporationId as number)
      .then((value) => {
        if (value.data) {
          setLocation(
            value.data.map((s) => ({
              key: s.branchID,
              name: s.name,
              color: s.color,
            }))
          );
        }
      });
  }, [selectedCompany]);

  return (
    <Modal
      open={open}
      title="Grant Granular Branch Access"
      okText="Add"
      cancelText="Cancel"
      confirmLoading={confirmLoading}
      onCancel={onCancel}
      onOk={() => {
        setConfirmLoading(true);
        form
          .validateFields()
          .then(async (values) => {
            // Insert / Invite Employee
            // to join the company.
            const result = await addEmployeeAccess(values);
            if (result) {
              form.resetFields();
              onCreate(result);
            } else {
              setShowError(true);
              // Stay on the Page and keep the Modal open and display Error message.
            }
          })
          .catch((info) => {
            setShowError(true);
            setConfirmLoading(false);
          });
        setConfirmLoading(false);
      }}
    >
      <Form
        form={form}
        layout="vertical"
        name="form_in_modal"
        initialValues={{ modifier: "public" }}
      >
        <Form.Item name="branchId">
          <Select
            placeholder="Select Employee Branch"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={location.map((loc) => {
              return {
                value: loc.key,
                label: loc.name,
              };
            })}
          />
        </Form.Item>
        <Form.Item
          name="roleId"
          hasFeedback
          rules={[
            {
              required: true,
              message: "Please select a role!",
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Select a role"
            allowClear
            optionFilterProp="children"
            options={roleOptions}
          />
        </Form.Item>
      </Form>
      {showError ? (
        <Alert
          message={
            errorMessage && errorMessage !== ""
              ? errorMessage
              : "There was an error processing your request. Please try again later."
          }
          type="error"
          showIcon
        />
      ) : null}
    </Modal>
  );
};

export default AddAccess;
